import { Close } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { enUS, pl } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NOTE_STATUSES, NoteStatus } from './VisitHistoryFilters';

/** Matches the org-wide history filters, so the two fields feel the same. */
const NAME_FILTER_DEBOUNCE_MS = 350;

/**
 * Below this the three controls stop fitting side by side — two date fields and
 * a status select each want roughly 170px before their labels start clipping —
 * so they stack instead.
 */
const SIDE_BY_SIDE_MIN_WIDTH = 560;

/** The statuses a patient's history is built from, in the order shown. */
export const HISTORY_STATUSES = ['COMPLETED', 'CANCELLED', 'ABORTED'] as const;

export type HistoryStatus = (typeof HISTORY_STATUSES)[number];

export interface ProfileHistoryFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  /** Zamknięto od — when the visit was closed, not when it was booked. */
  closedFrom?: Date | null;
  /** Zamknięto do. */
  closedTo?: Date | null;
  statuses: HistoryStatus[];
  noteStatuses: NoteStatus[];
  /** Free text matched against the professional's name. */
  professionalName?: string;
}

export const EMPTY_HISTORY_FILTERS: ProfileHistoryFilters = {
  startDate: null,
  endDate: null,
  closedFrom: null,
  closedTo: null,
  statuses: [],
  noteStatuses: [],
  professionalName: '',
};

interface Props {
  value: ProfileHistoryFilters;
  onChange: (next: ProfileHistoryFilters) => void;
  isOpen: boolean;
  /**
   * Which note states this viewer may filter by. Defaults to all three.
   *
   * The owner-facing card passes {@link OWNER_NOTE_STATUSES}: a draft is a note
   * the clinic is still writing and has deliberately not sent, and the API never
   * serialises one to an owner — `hasNotesDraft` is emitted only for
   * organisation-scoped callers. Offering Draft there would be an option that
   * matches nothing, and a promise that the owner can see something they must
   * not.
   */
  noteStatusOptions?: readonly NoteStatus[];
  /**
   * Offer a Specjalista field, first in the row.
   *
   * Off by default: on the patient card every visit in the list was performed by
   * the clinic reading it, and the filters there resolve server-side, where this
   * one has no query to bind to. The owner's history spans clinics and holds the
   * whole list in the browser, which is what makes "who did I see" a question
   * worth asking and cheap to answer.
   */
  showProfessionalFilter?: boolean;
}

/** The note states an owner can actually have on their own visits. */
export const OWNER_NOTE_STATUSES: readonly NoteStatus[] = ['SENT', 'NONE'];

/** The filters as the history endpoints take them. */
export interface ProfileHistoryQuery {
  startDate?: string;
  endDate?: string;
  closedFrom?: string;
  closedTo?: string;
  statuses?: string;
  noteStatuses?: string;
  professionalName?: string;
}

/**
 * The day the picker is showing, not the day it falls on in UTC.
 *
 * `toISOString()` would answer the second question: east of Greenwich a picked
 * midnight is the previous day in UTC, so "Data od 14/07" would ask the server
 * for the 13th and quietly include a day the professional did not select.
 */
const asDay = (date?: Date | null): string | undefined => {
  if (!date) return undefined;
  const day = new Date(date);
  if (Number.isNaN(day.getTime())) return undefined;
  const month = `${day.getMonth() + 1}`.padStart(2, '0');
  return `${day.getFullYear()}-${month}-${`${day.getDate()}`.padStart(2, '0')}`;
};

/**
 * Serialise the panel's state for the API. Shared by both history cards so the
 * two cannot disagree about what a filter asks for.
 */
export const toHistoryQuery = (
  filters: ProfileHistoryFilters,
): ProfileHistoryQuery => ({
  startDate: asDay(filters.startDate),
  endDate: asDay(filters.endDate),
  closedFrom: asDay(filters.closedFrom),
  closedTo: asDay(filters.closedTo),
  // An empty selection already means everything, so send nothing.
  statuses: filters.statuses.join(',') || undefined,
  noteStatuses: filters.noteStatuses.join(',') || undefined,
  professionalName: filters.professionalName?.trim() || undefined,
});

/**
 * Date range + status for one patient's visit history.
 *
 * Deliberately not the org-wide `VisitHistoryFilters`: that one filters by
 * owner and pet name, which mean nothing inside a single patient's card.
 */
export default function ProfileVisitHistoryFilters({
  value,
  onChange,
  isOpen,
  noteStatusOptions = NOTE_STATUSES,
  showProfessionalFilter = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('pl') ? pl : enUS;

  const statusLabel = (status: HistoryStatus) =>
    String(
      t(
        {
          COMPLETED: 'visit.historyFilterStatusCompleted',
          CANCELLED: 'visit.historyFilterStatusCancelled',
          ABORTED: 'visit.historyFilterStatusAborted',
        }[status],
      ),
    );

  const noteStatusLabel = (status: NoteStatus) =>
    String(
      t(
        {
          SENT: 'visit.notesStatusSent',
          DRAFT: 'visit.notesStatusDraft',
          NONE: 'visit.notesStatusNone',
        }[status],
      ),
    );

  const professionalName = value.professionalName ?? '';

  /**
   * The field keeps its own copy so typing stays responsive while the committed
   * filter — which is what reaches the server — settles.
   */
  const [professionalDraft, setProfessionalDraft] = useState(professionalName);
  const committedRef = useRef(professionalName);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  useEffect(() => {
    // Removing the chip or clearing everything changes the filter from outside
    // the field, and the field has to follow. Compared against what this
    // component last committed so its own commits do not bounce back and undo
    // whatever was typed in the meantime.
    if (professionalName !== committedRef.current) {
      committedRef.current = professionalName;
      setProfessionalDraft(professionalName);
    }
  }, [professionalName]);

  const handleProfessionalNameChange = (next: string) => {
    setProfessionalDraft(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      committedRef.current = next;
      onChange({ ...value, professionalName: next });
    }, NAME_FILTER_DEBOUNCE_MS);
  };

  const activeChips = [
    // First in the row of controls, so first in the row of chips.
    professionalName.trim() && {
      key: 'professionalName' as const,
      label: String(
        t('visit.historyProfessionalChip', { name: professionalName.trim() }),
      ),
    },
    value.startDate && {
      key: 'startDate' as const,
      label: String(
        t('visit.historyDateFromChip', {
          date: format(value.startDate, 'dd.MM.yyyy', { locale: dateLocale }),
        }),
      ),
    },
    value.endDate && {
      key: 'endDate' as const,
      label: String(
        t('visit.historyDateToChip', {
          date: format(value.endDate, 'dd.MM.yyyy', { locale: dateLocale }),
        }),
      ),
    },
    value.closedFrom && {
      key: 'closedFrom' as const,
      label: String(
        t('visit.historyClosedFromChip', {
          date: format(value.closedFrom, 'dd.MM.yyyy', { locale: dateLocale }),
        }),
      ),
    },
    value.closedTo && {
      key: 'closedTo' as const,
      label: String(
        t('visit.historyClosedToChip', {
          date: format(value.closedTo, 'dd.MM.yyyy', { locale: dateLocale }),
        }),
      ),
    },
    ...value.statuses.map((status) => ({
      key: status,
      label: statusLabel(status),
    })),
    ...(value.noteStatuses.length
      ? [
          {
            key: 'noteStatuses',
            label: String(
              t('visit.noteStatusChip', {
                statuses: value.noteStatuses.map(noteStatusLabel).join(', '),
              }),
            ),
          },
        ]
      : []),
  ].filter(Boolean) as Array<{ key: string; label: string }>;

  const removeChip = (key: string) => {
    if (key === 'professionalName')
      return onChange({ ...value, professionalName: '' });
    if (key === 'startDate') return onChange({ ...value, startDate: null });
    if (key === 'endDate') return onChange({ ...value, endDate: null });
    if (key === 'closedFrom') return onChange({ ...value, closedFrom: null });
    if (key === 'closedTo') return onChange({ ...value, closedTo: null });
    if (key === 'noteStatuses') return onChange({ ...value, noteStatuses: [] });
    return onChange({
      ...value,
      statuses: value.statuses.filter((status) => status !== key),
    });
  };

  /**
   * Every control is a third of the row, and rows are filled three at a time.
   *
   * Written as a list rather than as literal rows because the Specjalista field
   * shifts what lands where: without it the first row is the two dates and the
   * status, with it the status moves down to join Notatka. Hand-written rows
   * would mean two layouts to keep in step.
   */
  const controls = [
    showProfessionalFilter && (
      <TextField
        key="professionalName"
        label={t('search.book.cardSpecialist')}
        value={professionalDraft}
        onChange={(event) => handleProfessionalNameChange(event.target.value)}
        size="small"
        fullWidth
        sx={{ flex: '1 1 0' }}
        inputProps={{ 'data-testid': 'profile-history-professional-filter' }}
      />
    ),
    <DatePicker
      key="startDate"
      label={t('visit.historyDateFromLabel')}
      value={value.startDate ?? null}
      onChange={(startDate) => onChange({ ...value, startDate })}
      renderInput={(params) => (
        <TextField {...params} size="small" fullWidth sx={{ flex: '1 1 0' }} />
      )}
    />,
    <DatePicker
      key="endDate"
      label={t('visit.historyDateToLabel')}
      value={value.endDate ?? null}
      onChange={(endDate) => onChange({ ...value, endDate })}
      renderInput={(params) => (
        <TextField {...params} size="small" fullWidth sx={{ flex: '1 1 0' }} />
      )}
    />,
    <DatePicker
      key="closedFrom"
      label={t('visit.historyClosedFromLabel')}
      value={value.closedFrom ?? null}
      onChange={(closedFrom) => onChange({ ...value, closedFrom })}
      renderInput={(params) => (
        <TextField {...params} size="small" fullWidth sx={{ flex: '1 1 0' }} />
      )}
    />,
    <DatePicker
      key="closedTo"
      label={t('visit.historyClosedToLabel')}
      value={value.closedTo ?? null}
      onChange={(closedTo) => onChange({ ...value, closedTo })}
      renderInput={(params) => (
        <TextField {...params} size="small" fullWidth sx={{ flex: '1 1 0' }} />
      )}
    />,
    <FormControl key="statuses" size="small" sx={{ flex: '1 1 0' }}>
      <InputLabel id="profile-history-status-label">
        {t('visit.calendarFilterVisitStatusLabel')}
      </InputLabel>
      <Select
        multiple
        labelId="profile-history-status-label"
        label={t('visit.calendarFilterVisitStatusLabel')}
        value={value.statuses}
        onChange={(event) =>
          onChange({
            ...value,
            statuses: event.target.value as HistoryStatus[],
          })
        }
        // Nothing selected shows nothing: an empty filter already means
        // everything, and saying so takes a line to repeat it.
        renderValue={(selected) => selected.map(statusLabel).join(', ')}
      >
        {HISTORY_STATUSES.map((status) => (
          <MenuItem key={status} value={status}>
            <Checkbox checked={value.statuses.includes(status)} />
            <ListItemText primary={statusLabel(status)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>,
    <FormControl key="noteStatuses" size="small" sx={{ flex: '1 1 0' }}>
      <InputLabel id="profile-history-note-status-label">
        {t('visit.noteStatusFilterLabel')}
      </InputLabel>
      <Select
        multiple
        labelId="profile-history-note-status-label"
        label={t('visit.noteStatusFilterLabel')}
        value={value.noteStatuses}
        data-testid="profile-history-note-status-filter"
        onChange={(event) =>
          onChange({
            ...value,
            noteStatuses: event.target.value as NoteStatus[],
          })
        }
        // Nothing selected shows nothing: an empty filter already means
        // everything, and saying so takes a line to repeat it.
        renderValue={(selected) => selected.map(noteStatusLabel).join(', ')}
      >
        {noteStatusOptions.map((status) => (
          <MenuItem key={status} value={status}>
            <Checkbox checked={value.noteStatuses.includes(status)} />
            <ListItemText primary={noteStatusLabel(status)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>,
  ].filter(Boolean) as JSX.Element[];

  const rows: JSX.Element[][] = [];
  for (let index = 0; index < controls.length; index += 3) {
    rows.push(controls.slice(index, index + 3));
  }

  return (
    <>
      {/* The card is a fixed-height flex column, so without this the filter row
        is compressed to fit and its controls clipped. The table is what should
        give way — it scrolls inside itself. */}
      <Collapse in={isOpen} unmountOnExit sx={{ flexShrink: 0 }}>
        {/* Padded on all four sides: the outlined inputs carry floating labels
          that sit above their border, so with no top padding they touch the
          card header. */}
        {/* A container query, not a viewport breakpoint: this row lives in a
          column the professional can leave narrow while the window is wide, so
          `xs`/`sm` would answer the wrong question and keep three controls side
          by side in 400px. */}
        <Stack sx={{ px: 2, pt: 2, gap: 2, containerType: 'inline-size' }}>
          {rows.map((row, rowIndex) => (
            /* Three flex-1 children over two gaps on every row, so a control in
               the second row lines up with the one above it exactly rather than
               approximately. A short last row is padded with spacers that stand
               in for the controls that are not there. */
            <Stack
              key={rowIndex}
              sx={{
                flexDirection: 'row',
                gap: 2,
                alignItems: 'center',
                [`@container (max-width: ${SIDE_BY_SIDE_MIN_WIDTH}px)`]: {
                  // Stacked, each control takes the width like everything else
                  // and the spacers would only add empty rows.
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  '& .filter-row-spacer': { display: 'none' },
                },
              }}
            >
              {row}
              {Array.from({ length: 3 - row.length }, (_, spacerIndex) => (
                <Box
                  key={`spacer-${spacerIndex}`}
                  className="filter-row-spacer"
                  sx={{ flex: '1 1 0' }}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </Collapse>

      {/* Below the form, and outside the Collapse: they read as the result of
          the controls above them, and closing the panel must not hide which
          filters are still narrowing the table. */}
      {activeChips.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          gap={1}
          // The gap above the chips comes from here alone. If the form kept a
          // bottom padding too, opening the panel would add the two together
          // and the chips would sit lower than they do when it is closed.
          sx={{ p: 2, flexShrink: 0, justifyContent: 'flex-end' }}
        >
          {activeChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => removeChip(chip.key)}
              deleteIcon={<Close />}
            />
          ))}
          <Chip
            label={t('visit.filtersClearAll')}
            size="small"
            variant="outlined"
            onClick={() => onChange(EMPTY_HISTORY_FILTERS)}
            onDelete={() => onChange(EMPTY_HISTORY_FILTERS)}
            deleteIcon={<Close />}
          />
        </Stack>
      )}
    </>
  );
}
